/**
 * Where the buildings' sun shadows lie in each room, for the bodies standing in them: a body in a
 * building's shade is darkened like the ground under it, and casts no shadow of its own there (the
 * building already took the sun). Each storey's MapRenderingManager publishes its storey's shadow and
 * its strength this frame; a sprite asks about its feet. Pure: no pixi.
 */
import { insidePolygon, ShadowPolygon } from "./building_shadow_geometry"
import { ShadowStamp, stampCovers, stampQuad } from "./foliage_shadow_geometry"

interface RoomShade {
    owner: object
    polygons: readonly ShadowPolygon[]
    /** How many of the polygons, first, are the buildings' own (the rest are plants'). */
    buildings: number
    boxes: Float32Array
    stamps: readonly ShadowStamp[]
    stampBoxes: Float32Array
    alpha: number
    /** Where the light casts this frame (building_shadow.ts shadowLight): bodies cast their own shadows the same way. */
    sun?: { x: number; y: number }
    /** What the shade multiplies colours by (building_shadow.ts BUILDING_SHADOW.color). */
    color: number
    /** Which polygons (index) and stamps (-1 - index) overlap each BUCKET-px square, "bx,by". */
    buckets: Map<number, number[]>
}

const shades = new Map<string, RoomShade>()
let generation = 0

/** Changes whenever any room's shade is published or withdrawn: a body's lookups made before are stale. */
export const buildingShadeGeneration = (): number => generation

/** Bucketed by 64px squares: a lookup tests only the shapes near it (a forest casts thousands). */
const BUCKET = 64
const bucketKey = (bx: number, by: number) => (bx + 32768) * 65536 + (by + 32768)

const bucketsOf = (boxes: Float32Array, stampBoxes: Float32Array): Map<number, number[]> => {
    const buckets = new Map<number, number[]>()
    const add = (box: Float32Array, i: number, id: number) => {
        const b = i * 4
        for (let bx = Math.floor(box[b] / BUCKET); bx <= Math.floor(box[b + 2] / BUCKET); bx++) {
            for (let by = Math.floor(box[b + 1] / BUCKET); by <= Math.floor(box[b + 3] / BUCKET); by++) {
                const key = bucketKey(bx, by)
                const list = buckets.get(key)
                if (list) list.push(id)
                else buckets.set(key, [id])
            }
        }
    }
    for (let i = 0; i < boxes.length / 4; i++) add(boxes, i, i)
    for (let i = 0; i < stampBoxes.length / 4; i++) add(stampBoxes, i, -1 - i)
    return buckets
}

const boxesOf = (polygons: readonly ShadowPolygon[]): Float32Array => {
    const boxes = new Float32Array(polygons.length * 4)
    polygons.forEach((polygon, i) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let k = 0; k < polygon.length; k += 2) {
            minX = Math.min(minX, polygon[k]); maxX = Math.max(maxX, polygon[k])
            minY = Math.min(minY, polygon[k + 1]); maxY = Math.max(maxY, polygon[k + 1])
        }
        boxes.set([minX, minY, maxX, maxY], i * 4)
    })
    return boxes
}

/**
 * A storey's shadow, in its room's own pixels: its polygons, and the plants' silhouettes stamped on
 * the ground (foliage_shadow_geometry.ts). The first `buildings` polygons are the buildings' (the rest
 * the plants'). The latest publisher of a room owns it.
 */
export const publishBuildingShade = (roomId: string, owner: object, polygons: readonly ShadowPolygon[],
    stamps: readonly ShadowStamp[] = [], color = 0x000000, buildings = polygons.length): void => {
    const boxes = boxesOf(polygons), stampBoxes = boxesOf(stamps.map(stampQuad))
    shades.set(roomId, { owner, polygons, buildings, boxes, stamps, stampBoxes, buckets: bucketsOf(boxes, stampBoxes),
        alpha: shades.get(roomId)?.alpha ?? 0, sun: shades.get(roomId)?.sun, color })
    generation++
}

/** What a room's shade multiplies a colour by where it falls (black when nothing is published). */
export const buildingShadeColor = (roomId: string | undefined): number =>
    (roomId === undefined ? undefined : shades.get(roomId)?.color) ?? 0x000000

/** How strong the room's shade shows now (0: off, the sun's gone and the moon's not up, underground), undefined before a map publishes it. */
export const buildingShadeStrength = (roomId: string | undefined): number | undefined =>
    roomId === undefined ? undefined : shades.get(roomId)?.alpha

/** Where the room's light casts (the sun's way by day, the moon's by night), undefined before a map publishes it. */
export const buildingShadeSun = (roomId: string | undefined): { x: number; y: number } | undefined =>
    roomId === undefined ? undefined : shades.get(roomId)?.sun

/** How strong the shadow shows this frame (0: off, or underground), and which way its light casts. */
export const setBuildingShadeAlpha = (roomId: string, alpha: number, sun?: { x: number; y: number }): void => {
    const shade = shades.get(roomId)
    if (!shade) return
    shade.alpha = alpha
    if (sun) shade.sun = sun
}

export const withdrawBuildingShade = (roomId: string, owner: object): void => {
    if (contacts.get(roomId)?.owner === owner) {
        contacts.delete(roomId)
        generation++
    }
    if (shades.get(roomId)?.owner !== owner) return
    shades.delete(roomId)
    generation++
}

interface RoomContact {
    owner: object
    polygons: readonly ShadowPolygon[]
    boxes: Float32Array
    buckets: Map<number, number[]>
    color: number
    ratio: number
}

const contacts = new Map<string, RoomContact>()

/**
 * A storey's contact shade (castContactShadows: the dark gathered at the feet of what stands on it), as cast, for what
 * draws over its shade layers and must show it itself (a hill, hill_layer.ts): what it multiplies by, and how strong it
 * shows against the sun's shade (whose strength, buildingShadeStrength, it follows). Withdrawn with the room's shade.
 */
export const publishContactShade = (roomId: string, owner: object, polygons: readonly ShadowPolygon[], color = 0x000000, ratio = 1): void => {
    const boxes = boxesOf(polygons)
    contacts.set(roomId, { owner, polygons, boxes, buckets: bucketsOf(boxes, new Float32Array(0)), color, ratio })
    generation++
}

/** What a room's contact shade multiplies by, and its strength against the sun's shade; undefined when none is published. */
export const contactShadeOf = (roomId: string | undefined): { color: number; ratio: number } | undefined => {
    const contact = roomId === undefined ? undefined : contacts.get(roomId)
    return contact && { color: contact.color, ratio: contact.ratio }
}

/** Whether a point of a room lies in its published contact shade. */
export const inContactShade = (roomId: string | undefined, x: number, y: number): boolean => {
    const contact = roomId === undefined ? undefined : contacts.get(roomId)
    if (!contact) return false
    const { boxes, polygons } = contact
    for (const i of contact.buckets.get(bucketKey(Math.floor(x / BUCKET), Math.floor(y / BUCKET))) ?? []) {
        const b = i * 4
        if (x < boxes[b] || y < boxes[b + 1] || x > boxes[b + 2] || y > boxes[b + 3]) continue
        if (insidePolygon(polygons[i], x, y)) return true
    }
    return false
}

/**
 * Whether a point of a room lies in its published shade, however strong that shows now; `buildingsOnly`,
 * in the buildings' own shade (not the plants' or the furniture's).
 */
export const inBuildingShade = (roomId: string | undefined, x: number, y: number, buildingsOnly = false): boolean => {
    const shade = roomId === undefined ? undefined : shades.get(roomId)
    if (!shade) return false
    const { boxes, polygons, stampBoxes, stamps } = shade
    for (const id of shade.buckets.get(bucketKey(Math.floor(x / BUCKET), Math.floor(y / BUCKET))) ?? []) {
        if (buildingsOnly && (id < 0 || id >= shade.buildings)) continue
        const box = id >= 0 ? boxes : stampBoxes, i = id >= 0 ? id : -1 - id, b = i * 4
        if (x < box[b] || y < box[b + 1] || x > box[b + 2] || y > box[b + 3]) continue
        // A silhouette shades only where its art is drawn: a gap between leaf clusters lets the sun through.
        if (id >= 0 ? insidePolygon(polygons[i], x, y) : stampCovers(stamps[i], x, y)) return true
    }
    return false
}

/** How dark the buildings' shade is at a point of a room: 0 in the sun. */
export const buildingShadeAt = (roomId: string | undefined, x: number, y: number): number => {
    const shade = roomId === undefined ? undefined : shades.get(roomId)
    if (!shade || shade.alpha <= 0) return 0
    return inBuildingShade(roomId, x, y) ? shade.alpha : 0
}

/**
 * A tint darkened by a shade of strength `shade`, as the shade layer darkens the floor under it: each
 * channel times 1 - shade * (1 - color's channel). With a black `color`, simply times 1 - shade.
 */
export const shadeTint = (tint: number, shade: number, color = 0x000000): number => {
    if (shade <= 0) return tint
    const k = (c: number) => 1 - shade * (1 - c / 255)
    const r = Math.round(((tint >> 16) & 0xff) * k((color >> 16) & 0xff))
    const g = Math.round(((tint >> 8) & 0xff) * k((color >> 8) & 0xff))
    const b = Math.round((tint & 0xff) * k(color & 0xff))
    return (r << 16) | (g << 8) | b
}
